import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Textarea, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { adminSupportBulkImportSchema, AdminSupportBulkImportSchemaInput, AdminSupportBulkImportSchemaOutput } from "view/admin/utils/admin.schema";

import { SaveAdminFaqCategoriesDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";

interface SupportCategoryBulkImportDialogProps {
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

const SupportCategoryBulkImportDialog = ({ open, onClose, refetch }: SupportCategoryBulkImportDialogProps) => {

	const { handleSubmit, control, reset, formState } = useForm<AdminSupportBulkImportSchemaInput, unknown, AdminSupportBulkImportSchemaOutput>({
		resolver: zodResolver(adminSupportBulkImportSchema),
		defaultValues: {
			json: "",
		},
	});

	const [ saveAdminFaqCategories, { loading } ] = useMutation(SaveAdminFaqCategoriesDocument);

	const save = (data: AdminSupportBulkImportSchemaOutput) => {

		const categories = JSON.parse(data.json) as {
			id?: number;
			name: string;
			sequenceNumber: number;
		}[];

		saveAdminFaqCategories({
			variables: {
				inputs: categories.map(category => ({
					id: category.id,
					name: category.name,
					sequenceNumber: category.sequenceNumber,
				})),
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				json: "",
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title={intl.formatMessage({ id: "ADMIN_FAQ_CATEGORIES_BULK_IMPORT_TITLE" })}
			form="adminFaqCategoriesBulkImportForm"
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="adminFaqCategoriesBulkImportForm">
				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_FAQ_BULK_IMPORT_JSON_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="json"
						control={control}
						render={({ field }) => (
							<React.Fragment>
								<Textarea {...field} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default SupportCategoryBulkImportDialog;
